import { Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import getEarnedRevenueByDay from "../../api/auth/summaries/getEarnedRevenueByDay";
import { useSearchParams } from "react-router-dom";
import { addDays, endOfMonth, format, startOfMonth } from "date-fns";
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts";
import WushCard from "../../components/layout/WushCard";
import WushTextField from "../../components/form/WushTextField";
import getReservationRevenueByDay from "../../api/auth/summaries/getReservationRevenueByDay";
import PageLoading from "../../components/layout/PageLoading";
import toCurrency from "../../services/toCurrency";
import getGrossSalesByDay from "../../api/auth/summaries/getGrossSalesByDay";

export default function Revenue() {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultStartAt = searchParams.get('startAt') ? addDays(new Date(searchParams.get('startAt')), 1) : startOfMonth(new Date());
  const defaultEndAt = searchParams.get('endAt') ? addDays(new Date(searchParams.get('endAt')), 1) : endOfMonth(new Date());

  const [startAt, setStartAt] = useState(format(defaultStartAt, 'yyyy-MM-dd'));
  const [endAt, setEndAt] = useState(format(defaultEndAt, 'yyyy-MM-dd'));
  const [grossSales, setGrossSales] = useState();
  const [totalGrossSales, setTotalGrossSales] = useState();
  const [earnedRevenue, setEarnedRevenue] = useState();
  const [totalEarnedRevenue, setTotalEarnedRevenue] = useState();
  const [reservationRevenue, setReservationRevenue] = useState();
  const [totalReservationRevenue, setTotalReservationRevenue] = useState();

  const handleSetStartAt = (e) => {
    setStartAt(e.target.value);
    setSearchParams({ startAt: e.target.value, endAt: endAt });
  };

  const handleSetEndAt = (e) => {
    setEndAt(e.target.value);
    setSearchParams({ startAt: startAt, endAt: e.target.value });
  };

  useEffect(() => {
    getGrossSalesByDay({
      startDate: startAt,
      endDate: endAt,
    }, (response) => {
      console.log(response);
      setGrossSales(response.gross_sales_by_day);
      setTotalGrossSales(response.total);
    });
  }, [startAt, endAt]);

  useEffect(() => {
    getEarnedRevenueByDay({
      startDate: startAt,
      endDate: endAt,
    }, (response) => {
      console.log(response);
      setEarnedRevenue(response.earned_revenue_by_day);
      setTotalEarnedRevenue(response.total);
    });
  }, [startAt, endAt]);

  useEffect(() => {
    getReservationRevenueByDay({
      startDate: startAt,
      endDate: endAt,
    }, (response) => {
      console.log(response);
      setReservationRevenue(response.reservation_revenue_by_day);
      setTotalReservationRevenue(response.total);
    });
  }, [startAt, endAt]);

  const grossSalesData = () => Object.keys(grossSales).map((date) => {
    return {
      name: `${format(addDays(new Date(date), 1), 'MM-dd')}`,
      Revenue: grossSales[date] / 100
    };
  });

  const earnedRevenueData = () => Object.keys(earnedRevenue).map((date) => {
    return {
      name: `${format(addDays(new Date(date), 1), 'MM-dd')}`,
      Revenue: earnedRevenue[date] / 100
    };
  });

  const reservationRevenueData = () => Object.keys(reservationRevenue).map((date) => {
    return {
      name: `${format(addDays(new Date(date), 1), 'MM-dd')}`,
      Revenue: reservationRevenue[date] / 100
    };
  });

  if(!earnedRevenue || !reservationRevenue || !grossSales) return <PageLoading />;

  return (
    <Stack>
      <Stack mb={2}>
        <Typography variant="h4">Revenue Reports</Typography>
      </Stack>
      <Stack mb={6} width={{ xs: "100%", sm: "50%"}}>
        <Stack direction="row" mb={2} className="hide-print">
          <Button onClick={() => window.print()} variant="contained">
            Print
          </Button>
        </Stack>
        <WushCard>
          <Stack p={2}>
            <Stack direction="row" spacing={2}>
              <WushTextField type="date" label="Start Date" value={startAt} onChange={handleSetStartAt} />
              <WushTextField type="date" label="End Date" value={endAt} onChange={handleSetEndAt} />
              <Button variant="contained" color="secondary" className="hide-print" onClick={() => {
                setStartAt(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
                setEndAt(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
                setSearchParams({});
              }}>
                Reset
              </Button>
            </Stack>
          </Stack>
        </WushCard>
      </Stack>
      <Stack mb={5}>
        <Stack mb={2}>
          <Typography variant="h5">
            Gross Reservations: {toCurrency(totalGrossSales / 100)}
          </Typography>
          <Typography variant="body2">
            Gross reservations is total revenue made from sales and orders paid during the given time period. This includes taxes and fees. This number could include reservations that will take place in a future month.
          </Typography>
        </Stack>
        <BarChart width={1200} height={250} data={grossSalesData()}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar type="monotone" dataKey={"Revenue"} fill="#00b4d2" />
        </BarChart>
      </Stack>
      <Stack mb={5}>
        <Stack mb={2}>
          <Typography variant="h5">
            Earned revenue: {toCurrency(totalEarnedRevenue / 100)}
          </Typography>
          <Typography variant="body2">
            Earned revenue is total revenue made from reservations that ended during the given time period. This includes taxes.
          </Typography>
        </Stack>
        <BarChart width={1200} height={250} data={earnedRevenueData()}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar type="monotone" dataKey={"Revenue"} fill="#00b4d2" />
        </BarChart>
      </Stack>
      <Stack mb={5}>
        <Stack mb={2}>
          <Typography variant="h5">
            Reservation revenue: {toCurrency(totalReservationRevenue / 100)}
          </Typography>
          <Typography variant="body2">
            Reservation revenue is net revenue (after taxes and fees) from reservations that took place during the given time period.
          </Typography>
        </Stack>
        <BarChart width={1200} height={250} data={reservationRevenueData()}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar type="monotone" dataKey={"Revenue"} fill="#00b4d2" />
        </BarChart>
      </Stack>
    </Stack>
  );
}